import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Grid,
  Divider,
  Paper,
  Alert,
  AlertTitle,
  Button,
  Link
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BadgeIcon from '@mui/icons-material/Badge';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import ApiIcon from '@mui/icons-material/Api';
import TimerIcon from '@mui/icons-material/Timer';
import BackupIcon from '@mui/icons-material/Backup';
import BusinessIcon from '@mui/icons-material/Business';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LaunchIcon from '@mui/icons-material/Launch';
import EditIcon from '@mui/icons-material/Edit';

// Simple feature badge component
const FeatureBadge: React.FC<{ label: string; variant?: 'primary' | 'warning' | 'secondary' }> = ({ 
  label, 
  variant = 'primary' 
}) => (
  <Chip
    label={label}
    size="small"
    color={variant}
    variant="outlined"
    sx={{
      fontSize: '0.6rem',
      height: 18,
      ml: 1,
      '& .MuiChip-label': { px: 0.8 }
    }}
  />
);

const FeatureSection: React.FC<{
  icon: React.ReactElement;
  title: string;
  description: string;
  details?: string[];
  chips?: string[];
  color?: string;
  badgeLabel?: string;
  badgeVariant?: 'primary' | 'warning' | 'secondary';
  isReadOnly?: boolean;
}> = ({ icon, title, description, details, chips, color = 'primary.main', badgeLabel, badgeVariant = 'primary', isReadOnly = false }) => (
  <Grid item xs={12} md={6}>
    <Card sx={{ 
      height: '100%', 
      borderRadius: 2, 
      boxShadow: 3,
      opacity: isReadOnly ? 0.9 : 1,
      border: isReadOnly ? '1px solid #ff9800' : 'none'
    }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          {React.cloneElement(icon, { sx: { mr: 1.5, color: color, fontSize: '2rem' } })}
          <Typography variant="h6" component="div" sx={{ fontWeight: 500, color: color, flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          {badgeLabel && <FeatureBadge label={badgeLabel} variant={badgeVariant} />}
          {isReadOnly && <FeatureBadge label="Read-only in AppTesting" variant="warning" />}
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
          {description}
        </Typography>
        {details && (
          <List dense sx={{ mb: chips ? 1 : 0, pt: 0 }}>
            {details.map((detail, index) => (
              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {isReadOnly ? (
                    <VisibilityIcon fontSize="small" color="warning" />
                  ) : (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  )}
                </ListItemIcon>
                <ListItemText primary={detail} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>
        )}
        {chips && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
            {chips.map((chip, index) => (
              <Chip key={index} label={chip} size="small" variant="outlined" />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  </Grid>
);

const AppTesterDocumentation: React.FC = () => {
  return (
    <Box>
      {/* Introduction with Read-Only Emphasis */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'warning.lighter', borderLeft: '5px solid', borderColor: 'warning.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <VisibilityIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'warning.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
            App Tester Module - Read-Only Analysis
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          The <strong>App Tester</strong> provides comprehensive inspection, analysis, and testing of Microsoft Entra ID application registrations 
          in a secure read-only environment. Perfect for security audits, configuration reviews, and authentication testing without modification risks.
        </Typography>
        <Typography variant="body2" color="text.primary">
          Navigate through the sections using the left-hand side menu within the App Tester. Each section focuses on analyzing 
          specific areas of the application's configuration. <strong>For configuration changes, use the Azure Portal or upgrade to AppConfig².</strong>
        </Typography>
      </Paper>

      {/* Read-Only Mode Notice */}
      <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }}>
        <AlertTitle>Read-Only Mode - AppTesting</AlertTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          AppTesting provides comprehensive analysis capabilities without the ability to modify configurations. 
          This ensures a safe environment for testing and validation without accidental changes.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            endIcon={<LaunchIcon />}
            href="https://portal.azure.com"
            target="_blank"
            rel="noopener noreferrer"
            color="warning"
          >
            Make Changes in Azure Portal
          </Button>
          <Button
            variant="outlined"
            size="small"
            endIcon={<LaunchIcon />}
            href="https://appconfigapp.toncheetah.com"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            Learn About AppConfig² (Full Management)
          </Button>
        </Box>
      </Alert>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
        Configuration Analysis Sections
      </Typography>
      <Grid container spacing={3}>
        <FeatureSection
          icon={<SettingsIcon />}
          title="General Settings"
          description="View core application identifiers, platform configurations, supported account types, and credentials. All settings are displayed in read-only mode for analysis purposes."
          details={[
            "View App ID, Object ID, Tenant ID",
            "Analyze Redirect URIs configuration",
            "Review Client Secrets & Certificates (masked)",
            "Check Supported Account Types",
            "Examine endpoints (OAuth2, OpenID Connect, etc.)"
          ]}
          chips={["App IDs", "Redirect URIs", "Credentials", "Multi-tenant"]}
          color="info.main"
          isReadOnly
        />
        <FeatureSection
          icon={<AssignmentIcon />}
          title="Token Configuration"
          description="Analyze optional claims configuration and claims mapping policies. View how tokens are configured without the ability to modify settings."
          details={[
            "Analyze Optional Claims configuration",
            "Review Group Claims settings",
            "View assigned Claims Mapping Policies",
            "Understand Token Version (v1.0 vs v2.0) context"
          ]}
          chips={["ID Token", "Access Token", "SAML", "Claims Mapping", "Group Claims"]}
          color="secondary.main"
          isReadOnly
        />
        <FeatureSection
          icon={<BadgeIcon />}
          title="App Roles"
          description="Review defined application roles and their configurations. Analyze role definitions, values, and allowed member types in read-only mode."
          details={[
            "View all defined App Roles",
            "Analyze Role Values and Descriptions",
            "Review Allowed Member Types (Users/Groups, Applications)",
            "Understand role-based access patterns"
          ]}
          chips={["Custom Roles", "Permissions", "Authorization"]}
          color="success.main"
          isReadOnly
        />
        <FeatureSection
          icon={<VpnKeyIcon />}
          title="API Permissions"
          description="Analyze configured permissions for accessing other APIs. Review both delegated and application permissions with consent status."
          details={[
            "Review Delegated Permissions (User Context)",
            "Analyze Application Permissions (App Context)",
            "Check Admin Consent status",
            "View Permission Details and Descriptions"
          ]}
          chips={["Delegated Permissions", "Application Permissions", "OAuth2 Scopes", "Admin Consent"]}
          color="warning.main"
          isReadOnly
        />
        <FeatureSection
          icon={<ApiIcon />}
          title="Expose an API"
          description="Review how the application exposes its own API. Analyze custom scopes, Application ID URI, and authorized client applications."
          details={[
            "View Application ID URI",
            "Analyze defined OAuth2.0 Scopes",
            "Review Authorized Client Applications",
            "Understand API exposure configuration"
          ]}
          chips={["Resource API", "OAuth2 Scopes", "App ID URI", "Authorized Clients"]}
          color="error.main"
          isReadOnly
        />
        <FeatureSection
          icon={<PersonAddIcon />}
          title="User Assignments"
          description="View user and group assignments to the application. Analyze existing assignments and role distributions without modification capabilities."
          details={[
            "View assigned Users and Groups",
            "Analyze App Role assignments",
            "Review assignment patterns",
            "Understand access distribution"
          ]}
          chips={["User Assignment", "Group Assignment", "Role Assignment"]}
          color="primary.main"
          isReadOnly
        />
        <FeatureSection
          icon={<AdminPanelSettingsIcon />}
          title="Owners"
          description="View the list of application owners and their administrative privileges. Analyze ownership structure in read-only mode."
          details={[
            "View Current Application Owners",
            "Analyze ownership structure",
            "Review administrative access patterns"
          ]}
          chips={["Application Ownership", "Admin Privileges"]}
          color="info.dark"
          isReadOnly
        />
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 4, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
        Information & Analysis Sections
      </Typography>
      <Grid container spacing={3}>
        <FeatureSection
          icon={<SecurityIcon />}
          title="Conditional Access Analysis"
          description="Analyze Conditional Access Policies that apply to this application. Understand how these policies affect user sign-in and token issuance."
          details={[
            "Identify Applied CA Policies",
            "Analyze Policy Conditions (User, Location, Device)",
            "Review Grant Controls (MFA, Compliant Device)",
            "Understand security impact"
          ]}
          chips={["Conditional Access", "MFA", "Security Policies"]}
          color="secondary.dark"
        />
        <FeatureSection
          icon={<BusinessIcon />}
          title="Service Principal Analysis"
          description="Analyze the application's service principal object and its role in the tenant. Understand the enterprise application context."
          details={[
            "View Service Principal Object ID",
            "Analyze its Role in Authentication/Authorization",
            "Review Key Dates (Created, Modified)",
            "Understand tenant-specific configuration"
          ]}
          chips={["Service Principal", "Enterprise Application", "Instance"]}
          color="success.dark"
        />
        <FeatureSection
          icon={<TimerIcon />}
          title="Session Management Analysis"
          description="Review token lifetime and session behavior settings. Analyze how Conditional Access policies influence session management."
          details={[
            "Analyze Access Token Lifetime",
            "Review ID Token Lifetime",
            "Examine Sign-in Frequency Settings (if applicable)",
            "Understand session control impact"
          ]}
          chips={["Token Lifetime", "Session Control", "Sign-in Frequency"]}
          color="warning.dark"
        />
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 4, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
        Testing & Analysis Tools
      </Typography>
      <Grid container spacing={3}>
        <FeatureSection
          icon={<VpnKeyIcon />}
          title="Authentication Testing"
          description="Test authentication flows for this application in a safe, read-only environment. Acquire and analyze tokens without modifying application settings."
          details={[
            "Simulate User Sign-in flows",
            "Acquire ID Tokens and Access Tokens",
            "Decode and Inspect Token Contents",
            "Test with Different User Accounts (if applicable)"
          ]}
          chips={["Authentication Test", "Token Acquisition", "ID Token", "Access Token", "OIDC"]}
          color="error.dark"
        />
        <FeatureSection
          icon={<BackupIcon />}
          title="Configuration Export"
          badgeLabel="AppConfig² Only"
          description="Export application manifest for documentation and backup purposes. Restoration capabilities require AppConfig² or Azure Portal."
          details={[
            "Export Application Manifest (JSON)",
            "Document current configuration",
            "Create configuration snapshots",
            "Support for change tracking"
          ]}
          chips={["Export", "Documentation", "JSON Manifest", "Configuration Analysis"]}
          color="primary.dark"
        />
      </Grid>

      {/* Management Alternatives */}
      <Card sx={{ mt: 4, borderRadius: 2, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EditIcon sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="h6" color="info.dark">
              Making Configuration Changes
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            AppTesting provides comprehensive read-only analysis. To make configuration changes to your applications, 
            use one of these approaches:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Azure Portal
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  Use the Azure Portal for all management operations including registration, deletion, and configuration changes.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<LaunchIcon />}
                  href="https://portal.azure.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  fullWidth
                >
                  Open Azure Portal
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'secondary.main' }}>
                  PowerShell/CLI
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  Use Azure PowerShell or Azure CLI for programmatic configuration management and automation.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<LaunchIcon />}
                  href="https://docs.microsoft.com/azure/active-directory/develop/howto-create-service-principal-portal"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="secondary"
                  fullWidth
                >
                  View Documentation
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                  AppConfig²
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem' }}>
                  Get full management capabilities integrated with comprehensive analysis tools in one platform.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<LaunchIcon />}
                  href="https://appconfigapp.toncheetah.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="success"
                  fullWidth
                >
                  Learn More
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Updated Important Note */}
      <Paper elevation={0} sx={{ p: 3, mt: 4, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <InfoOutlinedIcon color="info" sx={{ mr: 1 }} />
          <Typography variant="h6" color="info.dark" sx={{ fontWeight: 500 }}>AppTesting - Read-Only Environment</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          AppTesting provides a secure, read-only environment for comprehensive application analysis, testing, and validation. 
          This approach ensures no accidental modifications to your production environment while offering complete visibility 
          into application configurations.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>To make configuration changes:</strong> Use the Azure Portal, Azure PowerShell/CLI, or upgrade to AppConfig² 
          for integrated management capabilities. This separation ensures that analysis and management activities are clearly 
          distinguished, supporting both security and operational best practices.
        </Typography>
      </Paper>
    </Box>
  );
};

export default AppTesterDocumentation;